import { auditChainCanonicalJson } from "../audit-archive-layout.js";
import { z } from "zod";
import { sha256, sha384 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
//#region src/kernel/kernel-encoding.ts
/**
* The canonical byte rules of the kernel contracts (kernel apps, WS-1a, R-042).
*
* The release statement, the rights certificate and the step-up digest read bytes through this
* module, so a signer and a verifier on another plane agree on every byte. The module holds four
* grammars and helpers:
*
* 1. the `sha256:<64 lower-case hex>` digest grammar and its hash function;
* 2. the SRI `sha384-<64 base64>` grammar and its hash function;
* 3. base64url with no padding (RFC 4648 section 5), in one canonical form;
* 4. canonical JSON, the audit chain form of `auditChainCanonicalJson`.
*
* The root barrel re-exports this module and browser bundles import that barrel, so this module
* imports no Node builtin and reads no Node global (`barrel-browser-safe.test.ts`). The hashes come
* from `@noble/hashes`. No schema here uses `.transform()`, so each one stays representable in the
* JSON Schema that `createOpenApiDocument()` writes.
*/
/** The text grammar of a SHA-256 digest: `sha256:` and 64 lower-case hex digits. One digest has one form. */
const SHA256_DIGEST_PATTERN = /^sha256:[0-9a-f]{64}$/;
/**
* The zod form of {@link SHA256_DIGEST_PATTERN}. It admits `sha256:` and 64 lower-case hex digits,
* and refuses upper-case hex, another length and another prefix. A template literal schema, so the
* JSON Schema form is `{ type: 'string', pattern: '^sha256:[0-9a-f]{64}$' }`.
*/
const sha256DigestSchema = z.templateLiteral(["sha256:", z.string().regex(/^[0-9a-f]{64}$/)]);
/** The SRI grammar of a SHA-384 hash: `sha384-` and the 64 standard base64 characters of 48 bytes. */
const SRI_SHA384_PATTERN = /^sha384-[A-Za-z0-9+/]{64}$/;
/**
* The zod form of {@link SRI_SHA384_PATTERN}. It admits `sha384-` and 64 standard base64
* characters, and refuses padding, the base64url alphabet and another length.
*/
const sriSha384Schema = z.templateLiteral(["sha384-", z.string().regex(/^[A-Za-z0-9+/]{64}$/)]);
/**
* The one error class of the kernel encoding rules. `code` is `not_base64url` for text that is not
* canonical base64url, and `not_canonical` for a value that canonical JSON refuses. A caller tests
* `instanceof KernelEncodingError` and reads `code`.
*/
var KernelEncodingError = class extends Error {
	code;
	constructor(code, message) {
		super(message);
		this.name = "KernelEncodingError";
		this.code = code;
	}
};
const BASE64URL_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const BASE64URL_TEXT = /^[A-Za-z0-9_-]*$/;
/**
* The base64url text of `bytes`: the RFC 4648 section 5 alphabet, with no padding. The empty
* array gives the empty text. Never throws. Pure.
*/
function base64UrlEncode(bytes) {
	let out = "";
	let i = 0;
	for (; i + 2 < bytes.length; i += 3) {
		const n = bytes[i] << 16 | bytes[i + 1] << 8 | bytes[i + 2];
		out += BASE64URL_ALPHABET[n >> 18 & 63] + BASE64URL_ALPHABET[n >> 12 & 63];
		out += BASE64URL_ALPHABET[n >> 6 & 63] + BASE64URL_ALPHABET[n & 63];
	}
	const rest = bytes.length - i;
	if (rest === 1) {
		const n = bytes[i] << 16;
		out += BASE64URL_ALPHABET[n >> 18 & 63] + BASE64URL_ALPHABET[n >> 12 & 63];
	} else if (rest === 2) {
		const n = bytes[i] << 16 | bytes[i + 1] << 8;
		out += BASE64URL_ALPHABET[n >> 18 & 63] + BASE64URL_ALPHABET[n >> 12 & 63];
		out += BASE64URL_ALPHABET[n >> 6 & 63];
	}
	return out;
}
/**
* The bytes of canonical base64url text. It admits the empty text and gives an empty array. It
* throws `KernelEncodingError('not_base64url')` in three cases, so one byte array has one text:
*
* 1. a character outside the RFC 4648 section 5 alphabet, `=` and `+` and `/` included;
* 2. a length of 1 modulo 4, which no byte count gives;
* 3. a last character whose unused low bits are not zero.
*
* Pure.
*/
function base64UrlDecode(text) {
	if (!BASE64URL_TEXT.test(text)) throw new KernelEncodingError("not_base64url", "base64UrlDecode: a character is outside the base64url alphabet");
	const tail = text.length % 4;
	if (tail === 1) throw new KernelEncodingError("not_base64url", "base64UrlDecode: a length of 1 modulo 4 is not base64url");
	const out = new Uint8Array(Math.floor(text.length / 4) * 3 + (tail === 0 ? 0 : tail - 1));
	let o = 0;
	let acc = 0;
	let bits = 0;
	for (const ch of text) {
		acc = (acc << 6 | BASE64URL_ALPHABET.indexOf(ch)) & 16777215;
		bits += 6;
		if (bits >= 8) {
			bits -= 8;
			out[o] = acc >> bits & 255;
			o += 1;
		}
	}
	if ((acc & (1 << bits) - 1) !== 0) throw new KernelEncodingError("not_base64url", "base64UrlDecode: the unused low bits of the last character are not zero");
	return out;
}
/** The `sha256:<hex>` digest of `bytes`. Any byte array, the empty one included. Never throws. Pure. */
function sha256DigestOf(bytes) {
	return `sha256:${bytesToHex(sha256(bytes))}`;
}
/**
* The SRI `sha384-<base64>` hash of `bytes`, the form of an HTML `integrity` attribute. Any byte
* array, the empty one included. Never throws. Pure.
*/
function sriSha384Of(bytes) {
	return `sha384-${base64UrlEncode(sha384(bytes)).replaceAll("-", "+").replaceAll("_", "/")}`;
}
/**
* The canonical JSON text of `value`: object keys in UTF-16 code unit order, `undefined` members
* omitted, no whitespace. It calls `auditChainCanonicalJson` of `audit-archive-layout.ts`, so the
* kernel contracts and the audit chain share one form. It throws
* `KernelEncodingError('not_canonical')` for a value that form refuses: a number that is not a
* safe integer, a `bigint` past the safe range, a function, a symbol or `undefined` outside an
* object member. Pure.
*/
function kernelCanonicalJson(value) {
	try {
		return auditChainCanonicalJson(value);
	} catch (error) {
		throw new KernelEncodingError("not_canonical", `kernelCanonicalJson: ${String(error)}`);
	}
}
//#endregion
export { KernelEncodingError, SHA256_DIGEST_PATTERN, SRI_SHA384_PATTERN, base64UrlDecode, base64UrlEncode, kernelCanonicalJson, sha256DigestOf, sha256DigestSchema, sriSha384Of, sriSha384Schema };

//# sourceMappingURL=kernel-encoding.js.map